import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Loader2, FolderOpen, ChevronDown, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  cloudSourceService,
  CloudSourceListRow,
} from '@/services/cloudSourceService';
import { logger } from '@/utils/logger';
import { getFriendlyErrorMessage } from '@/utils/errorMessage';
import FolderPickerDialog from './FolderPickerDialog';

interface ConfigModalProps {
  source: CloudSourceListRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void | Promise<void>;
  mandatory?: boolean;
}

// Mirrors orgain/kb/integrations/cloud_sources/sync.py:_ALLOWED_FILE_TYPES
// minus "other" - those are the extensions discovery actually downloads.
// The dropdown only offers these, so an unsupported value can't be picked.
export const SUPPORTED_EXTENSIONS = [
  'pdf', 'docx', 'doc', 'txt', 'md', 'rtf',
  'xlsx', 'xls', 'csv', 'pptx', 'ppt',
  'png', 'jpg', 'jpeg', 'gif', 'tiff', 'bmp',
];

// file_extension_filter is stored as a comma-separated string (e.g.
// ".pdf,.docx"); the backend re-normalises on save. In the UI we work with
// bare, lower-cased extensions.
const parseExtensions = (raw?: string | null): string[] =>
  (raw || '')
    .split(',')
    .map((s) => s.trim().toLowerCase().replace(/^\./, ''))
    .filter(Boolean);

/**
 * Tiny config form per the plan: folder list + extension filter. Anything
 * else (max file size, batch size, redaction policy) is global and not
 * exposed per-source.
 */
const CloudSourceConfigModal: React.FC<ConfigModalProps> = ({
  source,
  open,
  onOpenChange,
  onSaved,
  mandatory = false,
}) => {
  const isDrive = source.provider === 'Google Drive';
  const isConnected = source.connection_status === 'Connected';

  const [folderList, setFolderList] = useState(
    mandatory
      ? ''
      : isDrive ? source.drive_folder_ids || '' : source.sharepoint_folder_paths || '',
  );
  const [selectedExtensions, setSelectedExtensions] = useState<string[]>(
    () => (mandatory ? [] : parseExtensions(source.file_extension_filter)),
  );
  // Doctype default is 1 (included) - treat an absent value as checked.
  const [includeSubfolders, setIncludeSubfolders] = useState(
    source.include_subfolders !== 0,
  );
  const [saving, setSaving] = useState(false);
  // True while we ask the backend how many queued files a scope change would
  // clear (the pre-save dry run). Keeps the Save button in a loading state so
  // the round trip doesn't feel like a dead click.
  const [checkingScope, setCheckingScope] = useState(false);
  // Non-null => the "this will clear N queued files" confirmation is open.
  // Holds the exact count the purge will remove (from preview_scope_purge).
  const [pendingPurgeCount, setPendingPurgeCount] = useState<number | null>(null);
  // FolderPickerDialog toggle - the dialog itself owns the API calls
  // and selection state, then writes back via onConfirm.
  const [pickerOpen, setPickerOpen] = useState(false);

  const toggleExtension = (ext: string) => {
    setSelectedExtensions((prev) =>
      prev.includes(ext) ? prev.filter((e) => e !== ext) : [...prev, ext],
    );
  };

  // folderList is a comma-separated string; split into chips for display.
  const folderChips = folderList
    .split(',')
    .map((f) => f.trim())
    .filter(Boolean);

  const removeFolder = (folder: string) => {
    setFolderList(folderChips.filter((f) => f !== folder).join(', '));
  };

  // Order-independent equality for the comma-separated scope fields.
  const sortedKey = (items: string[]) => items.slice().sort().join('|');

  // Save entry point. When the change narrows the sync scope AND the source
  // has a queued backlog, first ask the backend exactly how many queued files
  // that scope change would purge (save_source clears them silently). If any
  // would be cleared, hold the save behind an explicit confirmation instead of
  // dropping the backlog with only an after-the-fact toast.
  const handleSave = async () => {
    const storedFolders = (isDrive
      ? source.drive_folder_ids
      : source.sharepoint_folder_paths) || '';
    const foldersChanged =
      sortedKey(
        storedFolders.split(',').map((f) => f.trim()).filter(Boolean),
      ) !== sortedKey(folderChips);
    const extsChanged =
      sortedKey(parseExtensions(source.file_extension_filter)) !==
      sortedKey(selectedExtensions);
    const subfoldersChanged =
      includeSubfolders !== (source.include_subfolders !== 0);
    const scopeAffecting = foldersChanged || extsChanged || subfoldersChanged;

    if (scopeAffecting && (source.pending_count ?? 0) > 0) {
      setCheckingScope(true);
      try {
        const res = await cloudSourceService.previewScopePurge(
          isDrive
            ? { source: source.name, drive_folder_ids: folderList.trim() }
            : { source: source.name, sharepoint_folder_paths: folderList.trim() },
        );
        if ((res.count ?? 0) > 0) {
          setPendingPurgeCount(res.count);
          return; // wait for the confirmation dialog
        }
      } catch (err) {
        // The preview is advisory: if it fails we fall through to the normal
        // save rather than block the user. The post-save toast still reports
        // anything actually cleared.
        logger.warn('Scope purge preview failed', err);
      } finally {
        setCheckingScope(false);
      }
    }
    await doSave();
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const payload: Parameters<typeof cloudSourceService.saveSource>[0] = {
        source: source.name,
        file_extension_filter: selectedExtensions.map((e) => `.${e}`).join(','),
        include_subfolders: includeSubfolders ? 1 : 0,
      };
      if (isDrive) {
        payload.drive_folder_ids = folderList.trim();
      } else {
        payload.sharepoint_folder_paths = folderList.trim();
      }
      payload.confirm_whole_drive_scan = folderList.trim() ? 0 : 1;
      const res = await cloudSourceService.saveSource(payload);
      // Scope change purges the queued backlog for folders no longer in
      // scope (backend save_source -> purge_out_of_scope_pending). Say so -
      // otherwise a large pending counter silently dropping to zero looks
      // like a bug, not a feature.
      if ((res.cleared_pending ?? 0) > 0) {
        toast.success(
          `Settings saved. ${res.cleared_pending} queued file${
            res.cleared_pending === 1 ? '' : 's'
          } from the previous folder scope ${
            res.cleared_pending === 1 ? 'was' : 'were'
          } cleared.`,
        );
      } else {
        toast.success('Settings saved. Changes will be applied during the next sync.');
      }
      await onSaved();
      onOpenChange(false);
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(err, 'Save failed');
      logger.error('Save source failed', err);
      toast.error('Save failed', { description: msg });
    } finally {
      setSaving(false);
    }
  };

  // User accepted the "clear N queued files" warning - close the prompt and
  // run the real save (which performs the purge and reports it via toast).
  const handleConfirmPurge = async () => {
    setPendingPurgeCount(null);
    await doSave();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!saving && !(mandatory && !o)) onOpenChange(o);
      }}
    >
      <DialogContent
        className="dark bg-background text-foreground sm:max-w-md"
        hideCloseButton={mandatory}
        onInteractOutside={mandatory ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={mandatory ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle>Configure {source.provider}</DialogTitle>
          <DialogDescription>
            Changes take effect on the next sync.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label>Folders</Label>
            <div
              role="button"
              tabIndex={isConnected ? 0 : -1}
              aria-disabled={!isConnected}
              onClick={() => isConnected && setPickerOpen(true)}
              onKeyDown={(e) => {
                if (isConnected && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  setPickerOpen(true);
                }
              }}
              className={`mt-1 flex min-h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                isConnected ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
              }`}
            >
              <div className="flex flex-1 flex-wrap items-center gap-1">
                {folderChips.length ? (
                  folderChips.map((f) => (
                    <Badge
                      key={f}
                      variant="secondary"
                      className="gap-1 pr-1 font-normal"
                    >
                      {f}
                      <button
                        type="button"
                        aria-label={`Remove ${f}`}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          removeFolder(f);
                        }}
                        className="rounded-sm opacity-60 hover:bg-black/10 hover:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground">
                    {isDrive
                      ? 'Whole account will be scanned'
                      : 'Whole drive will be scanned'}
                  </span>
                )}
              </div>
              <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
                <FolderOpen className="h-4 w-4" />
                Browse
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="include_subfolders"
              checked={includeSubfolders}
              onCheckedChange={(c) => setIncludeSubfolders(c === true)}
            />
            <Label
              htmlFor="include_subfolders"
              className="cursor-pointer font-normal"
            >
              Include subfolders
            </Label>
          </div>

          <div>
            <Label>File extension filter</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div
                  role="button"
                  tabIndex={0}
                  className="mt-1 flex min-h-10 w-full cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <div className="flex flex-1 flex-wrap items-center gap-1">
                    {selectedExtensions.length ? (
                      selectedExtensions.map((e) => (
                        <Badge
                          key={e}
                          variant="secondary"
                          className="gap-1 pr-1 font-normal"
                        >
                          .{e}
                          <button
                            type="button"
                            aria-label={`Remove .${e}`}
                            onPointerDown={(ev) => ev.stopPropagation()}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              toggleExtension(e);
                            }}
                            className="rounded-sm opacity-60 hover:bg-black/10 hover:opacity-100"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted-foreground">
                        Use workspace default
                      </span>
                    )}
                  </div>
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="dark bg-background text-foreground max-h-64 w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto"
              >
                {SUPPORTED_EXTENSIONS.map((ext) => (
                  <DropdownMenuCheckboxItem
                    key={ext}
                    checked={selectedExtensions.includes(ext)}
                    onCheckedChange={() => toggleExtension(ext)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    .{ext}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {selectedExtensions.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedExtensions([])}
                className="mt-1 text-xs text-red-400 underline hover:text-red-300"
              >
                Clear all ({selectedExtensions.length})
              </button>
            )}
          </div>
        </div>

        <DialogFooter>
          {!mandatory && (
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving || checkingScope}
            >
              Cancel
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving || checkingScope}>
            {(saving || checkingScope) && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save
          </Button>
        </DialogFooter>

        {/* Pre-save confirmation for a scope change that would purge queued
            files. Nested INSIDE DialogContent for the same DismissableLayer
            reason as the picker below - a sibling AlertDialog click would
            read as an interact-outside and dismiss the config modal. */}
        <AlertDialog
          open={pendingPurgeCount !== null}
          onOpenChange={(o) => {
            if (!o && !saving) setPendingPurgeCount(null);
          }}
        >
          <AlertDialogContent className="dark bg-background text-foreground">
            <AlertDialogHeader>
              <AlertDialogTitle>Clear queued files?</AlertDialogTitle>
              <AlertDialogDescription>
                This scope change drops{' '}
                <span className="font-medium text-foreground">
                  {pendingPurgeCount} queued file
                  {pendingPurgeCount === 1 ? '' : 's'}
                </span>{' '}
                that no longer match the selected folders. They haven&apos;t
                been imported yet, so nothing already in your knowledge base is
                affected - and any that come back into scope will be
                re-discovered on the next sync.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={saving}>
                Keep queued files
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmPurge} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Clear and save
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Nested INSIDE DialogContent (not a sibling) so Radix's
            DismissableLayer context registers the picker as a child layer of
            this modal. As a sibling, a click on the picker's Save button read
            as an "interact-outside" to this modal and dismissed it too - which
            unmounted the config modal mid-onConfirm, dropping the new folder
            selection and skipping the config modal's own save. */}
        {pickerOpen && (
          <FolderPickerDialog
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            sourceName={source.name}
            provider={source.provider}
            initialPaths={folderList}
            onConfirm={(paths) => {
              setFolderList(paths);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CloudSourceConfigModal;
